import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/context/AuthContext';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

export type Feedback = {
  id: string;
  candidate_name: string;
  job_title: string;
  positives?: string | null;
  improvements?: string | null;
  general_comment?: string | null;
  final_message?: string | null;
  created_at: string;
  certified?: boolean;
  feedback_type?: string | null;
  rating?: number | null;
  recruiter_name?: string | null;
  candidate_email?: string | null;
  recruiter_company?: string | null;
};

interface AccessCheckResult {
  has_access: boolean;
}

export const useFeedbackData = () => {
  const { id: urlFeedbackId } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [requiredLogin, setRequiredLogin] = useState(false);
  const [showCreateAccountPrompt, setShowCreateAccountPrompt] = useState(false);

  const feedbackId = urlFeedbackId || location.state?.feedbackId;

  console.log("useFeedbackData initialized with:", {
    urlFeedbackId,
    locationStateFeedbackId: location.state?.feedbackId,
    effectiveFeedbackId: feedbackId
  });

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd MMMM yyyy', { locale: fr });
    } catch (e) {
      console.error("Erreur lors du formatage de la date:", e);
      return dateString;
    }
  };

  useEffect(() => {
    const fetchFeedback = async () => {
      if (!feedbackId) {
        console.error("No feedback ID provided in URL or state", { 
          urlParam: urlFeedbackId,
          locationState: location.state
        });
        setError("Identifiant de feedback non fourni");
        setLoading(false);
        return;
      }

      console.log("Fetching feedback with ID:", feedbackId);
      
      try {
        const { data: feedbackData, error: feedbackError } = await supabase
          .from('feedbacks')
          .select('*')
          .eq('id', feedbackId)
          .maybeSingle();

        if (feedbackError) {
          console.error("Error fetching feedback:", feedbackError);
          setError("Une erreur est survenue lors de la récupération du feedback");
          setLoading(false);
          return;
        }

        if (!feedbackData) {
          console.log("No feedback found with ID:", feedbackId);
          setError("Feedback non trouvé");
          setLoading(false);
          return;
        }

        console.log("Feedback data:", feedbackData);
        setFeedback(feedbackData);

        if (user?.email) {
          console.log("Vérification de l'accès pour l'utilisateur:", user.email);
          
          if (feedbackData.user_id === user.id || 
              feedbackData.candidate_user_id === user.id ||
              (feedbackData.candidate_email && feedbackData.candidate_email.toLowerCase() === user.email.toLowerCase()) ||
              (feedbackData.recruiter_email && feedbackData.recruiter_email.toLowerCase() === user.email.toLowerCase())) {
            console.log("Accès direct accordé");
            setHasAccess(true);
            setFeedback(feedbackData);
            setLoading(false);
            return;
          }
          
          const { data: userProfile } = await supabase
            .from('profiles')
            .select('role')
            .eq('id', user.id)
            .maybeSingle();
            
          if (userProfile?.role === 'admin') {
            console.log("Accès admin accordé");
            setHasAccess(true);
            setFeedback(feedbackData);
            setLoading(false);
            return;
          }
          
          const { data: accessResult, error: accessError } = await supabase
            .rpc<AccessCheckResult>('check_feedback_access', {
              feedback_id: feedbackId,
              user_email: user.email
            });

          if (accessError) {
            console.error('Erreur lors de la vérification des droits d\'accès:', accessError);
            setError(`Erreur lors de la vérification des droits d'accès: ${accessError.message}`);
            setHasAccess(false);
          } else if (accessResult && accessResult.has_access) {
            console.log("Accès accordé via RPC");
            setHasAccess(true);
            setFeedback(feedbackData);
          } else {
            console.log("Accès refusé via RPC");
            setHasAccess(false);
          }
        } else if (feedbackData.status === 'private') {
          console.log("Feedback privé - connexion requise");
          setRequiredLogin(true);
          setHasAccess(false);
        } else {
          console.log("Feedback public - accès autorisé");
          setFeedback(feedbackData);
          setHasAccess(true);
        }
      } catch (error) {
        console.error("Error in fetchFeedback:", error);
        setError("Une erreur inattendue est survenue");
      } finally {
        setLoading(false);
      }
    };

    fetchFeedback();
  }, [feedbackId, user, navigate, location.state]);

  const isCandidateFeedback = feedback?.feedback_type === 'candidat';

  return {
    feedback,
    loading,
    error,
    hasAccess,
    requiredLogin,
    formatDate,
    isCandidateFeedback,
    canViewFeedback: hasAccess,
    showCreateAccountPrompt
  };
};
